/**
 * TrimHandle Component
 * Draggable control for adjusting audio clip start/end boundaries
 */
import { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { GripVertical } from 'lucide-react';

export const TrimHandle = ({
  type = 'start', // 'start' | 'end'
  onDrag,
  onDragStart,
  onDragEnd,
  isDragging = false,
  trimAmount = 0, // Amount trimmed in seconds (for tooltip)
}) => {
  const handleRef = useRef(null);
  const lastX = useRef(0);

  const handleMouseDown = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    lastX.current = e.clientX;
    onDragStart?.();
    
    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - lastX.current;
      lastX.current = moveEvent.clientX;
      onDrag?.(moveEvent, deltaX);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      onDragEnd?.();
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [onDrag, onDragStart, onDragEnd]);

  // Touch support
  const handleTouchStart = useCallback((e) => {
    e.stopPropagation();
    const touch = e.touches[0];
    lastX.current = touch.clientX;
    onDragStart?.();
    
    const handleTouchMove = (moveEvent) => {
      const touchMove = moveEvent.touches[0];
      const deltaX = touchMove.clientX - lastX.current;
      lastX.current = touchMove.clientX;
      onDrag?.(moveEvent, deltaX);
    };
    
    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      onDragEnd?.();
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [onDrag, onDragStart, onDragEnd]);

  const isLeft = type === 'start';

  return (
    <motion.div
      ref={handleRef}
      className={`
        absolute top-0 bottom-0 z-20
        w-3 
        flex items-center justify-center
        cursor-ew-resize
        transition-all
        group
        ${isLeft ? 'left-0 rounded-l-md' : 'right-0 rounded-r-md'}
        ${isDragging 
          ? 'bg-cyan-500/60 shadow-[0_0_12px_rgba(34,211,238,0.6)]' 
          : 'bg-cyan-500/30 hover:bg-cyan-500/50'
        }
      `}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      whileHover={{ width: 16 }}
      data-testid={`trim-handle-${type}`}
    >
      {/* Grip icon */}
      <GripVertical 
        className={`
          w-3 h-3 text-cyan-200/70
          group-hover:text-cyan-100
          ${isDragging ? 'text-cyan-100' : ''}
        `} 
        strokeWidth={2}
      />
      
      {/* Tooltip showing trim info */}
      {isDragging && (
        <motion.div 
          className={`
            absolute top-1/2 -translate-y-1/2
            px-2 py-1 
            bg-zinc-900/95 border border-cyan-500/50
            rounded-md
            text-[10px] font-mono text-cyan-300 
            whitespace-nowrap
            shadow-lg
            ${isLeft ? 'left-full ml-2' : 'right-full mr-2'}
          `}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          {type === 'start' ? 'Trim Start' : 'Trim End'}
          {trimAmount > 0 && (
            <span className="text-cyan-400 ml-1">
              {trimAmount.toFixed(2)}s
            </span>
          )}
        </motion.div>
      )}
      
      {/* Visual line indicator */}
      <div 
        className={`
          absolute top-0 bottom-0 w-px
          ${isDragging ? 'bg-cyan-400' : 'bg-cyan-500/50'}
          ${isLeft ? 'right-0' : 'left-0'}
        `}
      />
    </motion.div>
  );
};

export default TrimHandle;
